import React, { useRef, useState } from 'react';

import { BuilderComponent } from '@builder.io/react';
import { useSpring, animated } from '@react-spring/web';
import { useRouter } from 'next/router';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { BUILDER_MODELS } from '~/builder/constants/models';
import { trackBuilderTest } from '~/builder/utils/builderTesting';
import { useBreakpoint } from '~/techstyle-shared/react-components';

import getUrlPath from '../utils/getUrlPath';
import useBuilderCategoryNav from '../utils/useBuilderCategoryNav';

const Wrapper = styled.div`
  margin-bottom: 2rem;
`;

const CategoryDropdownContainer = styled.div`
  position: relative;
  z-index: 15;
`;

const CategoryDropdownButton = styled.button`
  width: 100%;
  font-weight: bold;
  font-size: 20px;
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gridCategoryTreeTextColor};
  padding: 19px 8px;
  border: none;
  background: ${({ theme }) => theme.colors.palette.white};
  border-bottom: ${({ $isExpanded }) => ($isExpanded ? '1px solid' : 'none')};
  position: relative;
  cursor: pointer;
  font-family: inherit;
  z-index: 150;
  letter-spacing: -0.5px;
  text-transform: capitalize;
`;

const Overlay = styled(animated.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

const Panel = styled(animated.div)`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform-origin: top center;
  z-index: 150;
  will-change: height;
`;

const IconWrapper = styled.span`
  padding-left: 4px;
  padding-top: 4px;
  display: flex;
`;

const CategoryTreeContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gridCategoryTreeTextColor};
  background-color: white;
  width: 100%;
  padding: 8px;
  box-shadow:
    0 4px 8px 0 rgba(0, 0, 0, 0.08),
    inset 0 1px 0 0 #e6e6e6;
`;

/**
 * Recursively finds the active block (active block meaning the ) from a list of Builder JSON blocks.
 *
 * @param {Object} params
 * @param {Array} params.builderJsonBlocks - An array of Builder JSON blocks to search through.
 * @param {string} params.currentPagePath - The current page path to match against the blocks' link paths.
 * @returns {Object|null} - The active block if found, otherwise null.
 *
 * @example
 * const builderJsonBlocks = [
 *   {
 *     component: { options: { linkPath: '/womens' } },
 *     children: [
 *       {
 *         component: { options: { linkPath: '/womens/bras' } },
 *         children: [],
 *       },
 *     ],
 *   },
 *   {
 *     component: { options: { linkPath: '/mens' } },
 *     children: [],
 *   },
 * ];
 *
 * const currentPagePath = '/womens/bras';
 *
 * const currentPageBuilderBlock = getCurrentPageBuilderBlock({
 *   builderJsonBlocks,
 *   currentPagePath,
 * });
 *
 * console.log(currentPageBuilderBlock);
 * // Output: { component: { options: { linkPath: '/womens/bras' } }, children: [] }
 */
function getCurrentPageBuilderBlock({
  builderJsonBlocks,
  currentPagePath: pagePath,
}) {
  if (!Array.isArray(builderJsonBlocks) || !pagePath) {
    return null;
  }

  for (const block of builderJsonBlocks) {
    const linkPath = block?.component?.options?.linkPath;

    if (linkPath) {
      const builderBlockLinkPath = getUrlPath({ url: linkPath });
      const currentPagePath = getUrlPath({ url: pagePath });

      // return current block if the current page path matches the
      // given Builder JSON defined `linkPath`
      if (currentPagePath === builderBlockLinkPath) {
        return block;
      }

      // if the current page path starts with the Builder JSON defined `linkPath`,
      // check the children blocks Builder JSON defined `linkPath` for a match
      if (currentPagePath.startsWith(builderBlockLinkPath) && block.children) {
        const result = getCurrentPageBuilderBlock({
          builderJsonBlocks: block.children,
          currentPagePath,
        });

        if (result) {
          return result;
        }
      }
    }

    // check children for the consumers provided `pagePath`
    // even if the current page path does not start with the
    // Builder JSON defined `linkPath`
    if (block.children) {
      const result = getCurrentPageBuilderBlock({
        builderJsonBlocks: block.children,
        currentPagePath: pagePath,
      });

      if (result) {
        return result;
      }
    }
  }

  // there is no active block (matching path) found
  return null;
}

function BuilderCategoryNavRenderer() {
  const { builderJson, isLoading, content, locale } = useBuilderCategoryNav();
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoint();
  const router = useRouter();

  const [isExpanded, setIsExpanded] = useState(false);

  // measure content for accordion height
  const contentRef = useRef(null);

  // spring for overlay opacity only
  const overlaySpring = useSpring({
    opacity: isExpanded ? 1 : 0,
    config: { tension: 250, friction: 30 },
  });

  // spring for panel height only
  const panelSpring = useSpring({
    height: isExpanded ? 'auto' : 0,
    config: { tension: 250, friction: 30 },
  });

  const builderStateData = {};

  let currentPageBuilderBlock = null;

  if (!isLoading) {
    currentPageBuilderBlock = getCurrentPageBuilderBlock({
      builderJsonBlocks: builderJson?.data?.blocks,
      currentPagePath: router.asPath,
    });
  }

  if (!currentPageBuilderBlock || isLoading) {
    return null;
  }

  return (
    <Wrapper>
      {isMobile && (
        <CategoryDropdownContainer>
          <CategoryDropdownButton
            data-expanded={isExpanded}
            onClick={() => setIsExpanded((v) => !v)}
          >
            {currentPageBuilderBlock?.component?.options?.linkText}
            <IconWrapper>
              {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
            </IconWrapper>
          </CategoryDropdownButton>

          {isExpanded && (
            <Overlay
              style={overlaySpring}
              onClick={() => setIsExpanded(false)}
            />
          )}

          <Panel style={panelSpring}>
            <div ref={contentRef}>
              <CategoryTreeContainer>
                <BuilderComponent
                  model={BUILDER_MODELS.CATEGORY_NAVIGATION}
                  content={content}
                  locale={locale}
                  data={builderStateData}
                  options={{
                    enrich: true,
                  }}
                  contentLoaded={(data, content) => {
                    trackBuilderTest(dispatch, content);
                  }}
                />
              </CategoryTreeContainer>
            </div>
          </Panel>
        </CategoryDropdownContainer>
      )}
      {!isMobile && (
        <BuilderComponent
          model={BUILDER_MODELS.CATEGORY_NAVIGATION}
          content={content}
          locale={locale}
          data={builderStateData}
          options={{
            enrich: true,
          }}
          contentLoaded={(data, content) => {
            trackBuilderTest(dispatch, content);
          }}
        />
      )}
    </Wrapper>
  );
}

BuilderCategoryNavRenderer.propTypes = {};

export default BuilderCategoryNavRenderer;
